import { reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { getBrandOptions } from '@js/api/domain/brand';
import { getInvoices } from '@js/api/domain/invoice';
import { getGraphicArtistOptions } from '@js/api/domain/user';
import { getErrorMessage } from "@js/api/utils/request-helpers";
const createDefaultFilters = () => ({
    brand_ids: [],
    statuses: ['open', 'paid', 'overdue'],
    due_date_from: '',
    due_date_to: '',
    created_from: '',
    created_to: '',
    graphic_artist_id: null,
    aging_bucket: null,
    invoice_number: '',
    po_filter: null,
    sort: 'newest',
    page: 1,
    per_page: 25,
});
const createDefaultPagination = () => ({
    total: 0,
    page: 1,
    per_page: 25,
    last_page: 1,
});
const mapToOptions = (items) => {
    if (!Array.isArray(items))
        return [];
    return items.map((item) => ({
        value: item.id,
        label: item.name,
    }));
};
export const useInvoiceListStore = defineStore('invoiceList', () => {
    const invoices = ref([]);
    const isLoading = ref(false);
    const isUserRestricted = ref(false);
    const error = ref(null);
    const filters = reactive(createDefaultFilters());
    // Filter dropdown options
    const clientManagerOptions = ref([]);
    const brandOptions = ref([]);
    const paginationData = reactive(createDefaultPagination());
    const fetchInvoices = async () => {
        isLoading.value = true;
        error.value = null;
        try {
            const { data, meta } = await getInvoices(filters);
            invoices.value = data.invoices;
            isUserRestricted.value = data.isUserRestricted;
            Object.assign(paginationData, meta);
        }
        catch (err) {
            error.value = getErrorMessage(err, 'Failed to fetch invoices');
            invoices.value = [];
        }
        finally {
            isLoading.value = false;
        }
    };
    const fetchClientManagerOptions = async () => {
        try {
            const { data } = await getGraphicArtistOptions();
            clientManagerOptions.value = mapToOptions(data);
        }
        catch (err) {
            console.error('Failed to load graphic artists:', err);
            clientManagerOptions.value = [];
        }
    };
    const fetchBrandOptions = async () => {
        try {
            const { data } = await getBrandOptions();
            brandOptions.value = mapToOptions(data);
        }
        catch (err) {
            console.error('Failed to load available brands:', err);
            brandOptions.value = [];
        }
    };
    // Pagination & Filter Handlers
    const setPage = (page) => {
        filters.page = page;
        fetchInvoices();
    };
    const setPerPage = (perPage) => {
        filters.per_page = perPage;
        filters.page = 1;
        fetchInvoices();
    };
    const applyFilters = () => {
        filters.page = 1;
        fetchInvoices();
    };
    const resetFilters = () => {
        Object.assign(filters, createDefaultFilters());
        Object.assign(paginationData, createDefaultPagination());
        fetchInvoices();
    };
    return {
        invoices,
        isUserRestricted,
        paginationData,
        isLoading,
        error,
        filters,
        clientManagerOptions,
        brandOptions,
        fetchInvoices,
        fetchClientManagerOptions,
        fetchBrandOptions,
        setPage,
        setPerPage,
        applyFilters,
        resetFilters,
    };
});
